import React, { useMemo, useState } from 'react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, FormRow, PageHeader, ProgressBar, StatusBadge } from '../../components/ui';
import { normalizeUserRoles } from '../../utils/navigation';
import { assignmentGrade, byId, derivedAssignmentStatus, fullName, getAssignmentProgress, getCourse, getProgramProgress, programGrade } from '../../utils/lmsSelectors';

function matches(text, query) {
  return String(text || '').toLowerCase().includes(query.trim().toLowerCase());
}

function SearchableCheckboxList({ title, items, selectedIds, onToggle, search, setSearch, getLabel, getMeta, onSelectVisible }) {
  const filtered = search ? items.filter((item) => matches(`${getLabel(item)} ${getMeta?.(item) || ''}`, search)) : items;
  const allVisibleSelected = filtered.length > 0 && filtered.every((item) => selectedIds.includes(item.id));
  return (
    <div>
      <div className="selection-heading">
        <strong>{title}</strong>
        <input className="search-input" placeholder={`Search ${title.toLowerCase()}`} value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="check-row select-all-row">
          <input type="checkbox" checked={allVisibleSelected} onChange={() => onSelectVisible(filtered, allVisibleSelected)} />
          Select all visible
        </label>
      </div>
      <div className="checkbox-grid scroll-box">
        {filtered.map((item) => (
          <label key={item.id} className="check-row">
            <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => onToggle(item.id)} />
            <span>{getLabel(item)}{getMeta && <small className="muted"> • {getMeta(item)}</small>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function AssignmentFilters({ filters, setFilters, type }) {
  return (
    <div className="assignment-filter-bar">
      <input
        className="search-input"
        placeholder={type === 'program' ? 'Search learner, program, status, or due date' : 'Search learner, course, source, status, or due date'}
        value={filters.query}
        onChange={(e) => setFilters({ ...filters, query: e.target.value })}
      />
      <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
        <option value="all">All statuses</option>
        <option value="not_started">Not Started</option>
        <option value="in_progress">In Progress</option>
        <option value="needs_review">Needs Review</option>
        <option value="completed">Completed</option>
        <option value="overdue">Overdue</option>
        <option value="revoked">Revoked</option>
      </select>
    </div>
  );
}

export default function AdminAssignments() {
  const {
    data,
    currentUser,
    assignCourse,
    assignProgram,
    revokeAssignment,
    revokeProgramAssignment,
    deleteAssignment,
    deleteProgramAssignment,
    updateAssignmentDueDate,
    updateProgramAssignmentDueDate
  } = useLms();
  const firstPublishedCourse = data.courses.find((course) => course.status === 'published')?.id || '';
  const firstPublishedProgram = (data.programs || []).find((program) => program.status === 'published')?.id || '';
  const [form, setForm] = useState({ type: 'course', courseId: firstPublishedCourse, programId: firstPublishedProgram, dueDate: '', userIds: [], groupIds: [] });
  const [message, setMessage] = useState('');
  const [learnerSearch, setLearnerSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const [courseFilters, setCourseFilters] = useState({ query: '', status: 'all' });
  const [programFilters, setProgramFilters] = useState({ query: '', status: 'all' });
  const [dueDateEdit, setDueDateEdit] = useState({ type: '', id: '', value: '' });
  const learners = data.users.filter((user) => normalizeUserRoles(user).includes('learner') && user.status === 'active');
  const publishedCourses = data.courses.filter((course) => course.status === 'published');
  const publishedPrograms = (data.programs || []).filter((program) => program.status === 'published');
  const filteredCourses = courseSearch ? publishedCourses.filter((course) => matches(`${course.title} ${course.category} ${course.status}`, courseSearch)) : publishedCourses;
  const filteredPrograms = programSearch ? publishedPrograms.filter((program) => matches(`${program.title} ${program.category} ${program.status}`, programSearch)) : publishedPrograms;

  const toggle = (field, id) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].includes(id) ? current[field].filter((item) => item !== id) : [...current[field], id]
    }));
  };

  const toggleVisible = (field, visibleItems, allSelected) => {
    setForm((current) => {
      const visibleIds = visibleItems.map((item) => item.id);
      return {
        ...current,
        [field]: allSelected
          ? current[field].filter((id) => !visibleIds.includes(id))
          : [...new Set([...current[field], ...visibleIds])]
      };
    });
  };

  const submit = (event) => {
    event.preventDefault();
    if (!form.dueDate) {
      setMessage('Due date is required before creating an assignment.');
      return;
    }
    const payload = form.type === 'program'
      ? { programId: form.programId, dueDate: form.dueDate, userIds: form.userIds, groupIds: form.groupIds }
      : { courseId: form.courseId, dueDate: form.dueDate, userIds: form.userIds, groupIds: form.groupIds };
    const result = form.type === 'program' ? assignProgram(payload, currentUser.id) : assignCourse(payload, currentUser.id);
    if (form.type === 'program') {
      setMessage(result.ok ? `Created ${result.created.length} program assignment(s) and ${result.createdCourseAssignments.length} course assignment(s). Existing duplicates were skipped.` : result.message);
    } else {
      setMessage(result.ok ? `Created ${result.created.length} assignment(s). Existing duplicates were skipped.` : result.message);
    }
  };

  const courseRows = data.assignments.map((assignment) => {
    const learner = byId(data.users, assignment.assignedToUserId);
    const course = getCourse(data, assignment.courseId);
    return {
      ...assignment,
      learner: fullName(learner),
      course: course?.title,
      source: assignment.programId ? 'Program' : assignment.assignmentSource,
      statusValue: derivedAssignmentStatus(data, assignment),
      progress: getAssignmentProgress(data, assignment).percent,
      grade: assignmentGrade(data, assignment)
    };
  });

  const programRows = useMemo(() => (data.programAssignments || []).map((assignment) => {
    const learner = byId(data.users, assignment.assignedToUserId);
    const program = byId(data.programs || [], assignment.programId);
    const progress = getProgramProgress(data, assignment);
    return {
      ...assignment,
      learner: fullName(learner),
      program: program?.title,
      statusValue: assignment.status === 'revoked' ? 'revoked' : progress.status,
      progress: progress.percent,
      grade: programGrade(data, assignment)
    };
  }), [data]);

  const visibleCourseRows = courseRows.filter((row) => {
    const statusMatch = courseFilters.status === 'all' || row.statusValue === courseFilters.status;
    const textMatch = !courseFilters.query || matches(`${row.learner} ${row.course} ${row.source} ${row.statusValue} ${row.dueDate}`, courseFilters.query);
    return statusMatch && textMatch;
  });

  const visibleProgramRows = programRows.filter((row) => {
    const statusMatch = programFilters.status === 'all' || row.statusValue === programFilters.status;
    const textMatch = !programFilters.query || matches(`${row.learner} ${row.program} ${row.statusValue} ${row.dueDate}`, programFilters.query);
    return statusMatch && textMatch;
  });

  const handleDeleteAssignment = (row) => {
    if (!window.confirm(`Delete revoked assignment for ${row.learner}? This cannot be undone.`)) return;
    const result = deleteAssignment(row.id, currentUser.id);
    setMessage(result.ok ? 'Revoked assignment deleted.' : result.message);
  };

  const handleDeleteProgramAssignment = (row) => {
    if (!window.confirm(`Delete revoked program assignment for ${row.learner}? This will also delete its related course assignments.`)) return;
    const result = deleteProgramAssignment(row.id, currentUser.id);
    setMessage(result.ok ? 'Revoked program assignment deleted.' : result.message);
  };

  const startDueDateEdit = (type, row) => {
    setDueDateEdit({ type, id: row.id, value: row.dueDate || '' });
  };

  const cancelDueDateEdit = () => {
    setDueDateEdit({ type: '', id: '', value: '' });
  };

  const saveDueDateEdit = (type, row) => {
    const nextDueDate = String(dueDateEdit.value || '').trim();
    if (!nextDueDate) {
      setMessage('Due date is required. Please select a date before saving.');
      return;
    }

    const result = type === 'program'
      ? updateProgramAssignmentDueDate(row.id, nextDueDate, currentUser.id)
      : updateAssignmentDueDate(row.id, nextDueDate, currentUser.id);

    if (result.ok) {
      setMessage(type === 'program'
        ? 'Program assignment due date updated. Related course assignment due dates were also updated.'
        : row.programAssignmentId
          ? 'Program-sourced assignment due date updated. Related course assignment due dates were also updated.'
          : 'Course assignment due date updated.');
      cancelDueDateEdit();
    } else {
      setMessage(result.message);
    }
  };

  const renderDueDateCell = (type, row) => {
    const isEditing = dueDateEdit.type === type && dueDateEdit.id === row.id;
    if (isEditing) {
      return (
        <div className="due-date-edit">
          <input
            required
            type="date"
            value={dueDateEdit.value}
            onChange={(e) => setDueDateEdit({ ...dueDateEdit, value: e.target.value })}
          />
          <div className="button-row compact">
            <button type="button" className="button button-primary" onClick={() => saveDueDateEdit(type, row)}>Save</button>
            <button type="button" className="button button-ghost" onClick={cancelDueDateEdit}>Cancel</button>
          </div>
        </div>
      );
    }

    return (
      <div className="due-date-view">
        <strong>{row.dueDate || 'No due date'}</strong>
        <button type="button" className="button button-ghost" onClick={() => startDueDateEdit(type, row)}>Edit</button>
        {type === 'course' && row.programAssignmentId && <small className="muted">Program-linked</small>}
      </div>
    );
  };

  return (
    <>
      <PageHeader title="Assignments" eyebrow="Admin" description="Assign published courses or bundled programs to learners and groups using searchable selectors." />
      <div className="grid two uneven">
        <Card>
          <h2>Assignment wizard</h2>
          <form className="form-stack" onSubmit={submit}>
            <FormRow label="Assignment type">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="course">Course</option>
                <option value="program">Program / Bundle</option>
              </select>
            </FormRow>
            {form.type === 'course' ? (
              <div className="selector-panel">
                <FormRow label="Search course"><input className="search-input" placeholder="Search by title, category, trainer, or status" value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} /></FormRow>
                <FormRow label="Published course"><select value={form.courseId} onChange={(e) => setForm({ ...form, courseId: e.target.value })}>{filteredCourses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}</select></FormRow>
              </div>
            ) : (
              <div className="selector-panel">
                <FormRow label="Search program"><input className="search-input" placeholder="Search by title, category, or status" value={programSearch} onChange={(e) => setProgramSearch(e.target.value)} /></FormRow>
                <FormRow label="Published program"><select value={form.programId} onChange={(e) => setForm({ ...form, programId: e.target.value })}>{filteredPrograms.map((program) => <option key={program.id} value={program.id}>{program.title}</option>)}</select></FormRow>
              </div>
            )}
            <FormRow label="Due date"><input required type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} /></FormRow>
            <SearchableCheckboxList
              title="Individual learners"
              items={learners}
              selectedIds={form.userIds}
              onToggle={(id) => toggle('userIds', id)}
              search={learnerSearch}
              setSearch={setLearnerSearch}
              getLabel={fullName}
              getMeta={(user) => `${user.email} • ${user.team || 'No team'}`}
              onSelectVisible={(visible, allSelected) => toggleVisible('userIds', visible, allSelected)}
            />
            <SearchableCheckboxList
              title="Groups"
              items={data.groups}
              selectedIds={form.groupIds}
              onToggle={(id) => toggle('groupIds', id)}
              search={groupSearch}
              setSearch={setGroupSearch}
              getLabel={(group) => group.name}
              getMeta={(group) => `${group.memberIds?.length || 0} members`}
              onSelectVisible={(visible, allSelected) => toggleVisible('groupIds', visible, allSelected)}
            />
            <button className="button button-primary" type="submit" disabled={!form.dueDate || (form.type === 'course' ? !form.courseId : !form.programId)}>{form.type === 'course' ? 'Assign course' : 'Assign program'}</button>
            {message && <p className="feedback-box compact-feedback">{message}</p>}
          </form>
        </Card>
        <Card>
          <h2>Assignment rules</h2>
          <ul className="check-list">
            <li>Due date is required. Assignments cannot be created with a blank due date.</li>
            <li>Draft and archived courses/programs cannot be assigned.</li>
            <li>Duplicate active course and program assignments are prevented.</li>
            <li>Admins can edit assignment due dates from the program and course assignment registers.</li>
            <li>Revoked assignments can be deleted from the register.</li>
            <li>Certificates are generated only after all course requirements are completed.</li>
          </ul>
        </Card>
      </div>
      <Card>
        <div className="section-heading">
          <div>
            <h2>Program assignment register</h2>
            <p className="muted small">Showing {visibleProgramRows.length} of {programRows.length} program assignments.</p>
          </div>
        </div>
        <AssignmentFilters filters={programFilters} setFilters={setProgramFilters} type="program" />
        <DataTable
          rows={visibleProgramRows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'program', label: 'Program' },
            { key: 'dueDate', label: 'Due date', render: (row) => renderDueDateCell('program', row) },
            { key: 'statusValue', label: 'Status', render: (row) => <StatusBadge status={row.statusValue} /> },
            { key: 'progress', label: 'Progress', render: (row) => <ProgressBar value={row.progress} /> },
            { key: 'grade', label: 'Program grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` },
            { key: 'action', label: 'Action', render: (row) => (
              <div className="button-row compact">
                <button type="button" className="button button-ghost" disabled={row.status === 'revoked'} onClick={() => revokeProgramAssignment(row.id, currentUser.id)}>Revoke</button>
                {row.status === 'revoked' && <button type="button" className="button button-danger" onClick={() => handleDeleteProgramAssignment(row)}>Delete</button>}
              </div>
            ) }
          ]}
          emptyMessage="No program assignments match the filter."
        />
      </Card>
      <Card>
        <div className="section-heading">
          <div>
            <h2>Course assignment register</h2>
            <p className="muted small">Showing {visibleCourseRows.length} of {courseRows.length} course assignments.</p>
          </div>
        </div>
        <AssignmentFilters filters={courseFilters} setFilters={setCourseFilters} type="course" />
        <DataTable
          rows={visibleCourseRows}
          columns={[
            { key: 'learner', label: 'Learner' },
            { key: 'course', label: 'Course' },
            { key: 'source', label: 'Source' },
            { key: 'dueDate', label: 'Due date', render: (row) => renderDueDateCell('course', row) },
            { key: 'statusValue', label: 'Status', render: (row) => <StatusBadge status={row.statusValue} /> },
            { key: 'progress', label: 'Progress', render: (row) => <ProgressBar value={row.progress} /> },
            { key: 'grade', label: 'Grade', render: (row) => row.grade === null ? '—' : `${row.grade}%` },
            { key: 'action', label: 'Action', render: (row) => (
              <div className="button-row compact">
                <button type="button" className="button button-ghost" disabled={row.status === 'revoked'} onClick={() => revokeAssignment(row.id, currentUser.id)}>Revoke</button>
                {row.status === 'revoked' && <button type="button" className="button button-danger" onClick={() => handleDeleteAssignment(row)}>Delete</button>}
              </div>
            ) }
          ]}
          emptyMessage="No course assignments match the filter."
        />
      </Card>
    </>
  );
}
